import { useEffect, useState } from "react";
import { Music2 } from "lucide-react";
import { Slider } from "@/components/ui/slider";
import { fmt } from "@/lib/api";
import Equalizer from "./Equalizer";
import PlayerControls from "./PlayerControls";

const SOURCE = { youtube: "YouTube", spotify: "Spotify", soundcloud: "SoundCloud" };

function useLivePosition(player) {
  const [pos, setPos] = useState(player?.position || 0);
  useEffect(() => {
    setPos(player?.position || 0);
    if (!player?.playing || player?.paused) return undefined;
    const start = Date.now();
    const base = player.position;
    const t = setInterval(() => setPos(Math.min(base + Date.now() - start, player.current?.length || 0)), 500);
    return () => clearInterval(t);
  }, [player]);
  return pos;
}

export default function NowPlaying({ player, act }) {
  const cur = player?.current;
  const pos = useLivePosition(player);
  const live = cur && player.playing && !player.paused;

  return (
    <section className="card p-5 sm:p-6 lg:col-span-7 relative overflow-hidden fade-up" data-testid="now-playing-card">
      {cur?.artwork && (
        <img src={cur.artwork} alt="" className="absolute inset-0 h-full w-full object-cover opacity-[0.07] blur-2xl scale-125" />
      )}
      <div className="relative">
        <div className="flex items-center justify-between">
          <div className="caption">Teraz gra</div>
          <div className="flex items-center gap-3">
            {player?.connected && (
              <button
                onClick={() => act("stay", !player.stay_247)}
                data-testid="toggle-247-button"
                title="Tryb 24/7: bot zostaje na kanale i gra bez przerwy"
                className={`rounded-full border px-2.5 py-0.5 text-[11px] font-mono transition-colors ${
                  player.stay_247 ? "border-indigo-400/60 bg-indigo-500/20 text-indigo-200" : "border-slate-700 text-slate-400 hover:border-slate-500"
                }`}
              >
                24/7 {player.stay_247 ? "WŁ" : "WYŁ"}
              </button>
            )}
            {player?.connected && (
              <span className="text-xs font-mono text-slate-400" data-testid="voice-channel-name">🔊 {player.channel_name}</span>
            )}
            <Equalizer active={live} />
          </div>
        </div>

        <div className="mt-5 flex flex-col sm:flex-row gap-5">
          <div className="relative h-36 w-36 sm:h-40 sm:w-40 shrink-0 rounded-xl overflow-hidden border border-slate-700 bg-slate-950 grid place-items-center artwork-glow">
            {cur?.artwork ? (
              <img src={cur.artwork} alt="" className="h-full w-full object-cover" data-testid="now-playing-artwork" />
            ) : (
              <Music2 className="h-10 w-10 text-slate-600" />
            )}
          </div>
          <div className="min-w-0 flex-1 flex flex-col justify-center">
            <a
              href={cur?.uri || undefined}
              target="_blank"
              rel="noreferrer"
              className="font-heading text-2xl sm:text-3xl font-bold tracking-tight line-clamp-2 hover:text-emerald-300 transition-colors"
              data-testid="now-playing-title"
            >
              {cur ? cur.title : player?.connected ? "Cisza w kolejce" : "Bot nie jest na kanale"}
            </a>
            <div className="text-slate-400 mt-1 truncate" data-testid="now-playing-author">
              {cur ? cur.author : "Dodaj utwór poniżej lub wpisz !play na Discordzie"}
            </div>
            {cur && (
              <div className="mt-3 flex flex-wrap gap-2 text-[11px] font-mono">
                <span className="rounded-full border border-slate-700 px-2 py-0.5 text-slate-300">{SOURCE[cur.source] || cur.source}</span>
                {cur.requester && <span className="rounded-full border border-indigo-500/40 text-indigo-300 px-2 py-0.5">od: {cur.requester}</span>}
              </div>
            )}
          </div>
        </div>

        <div className="mt-6">
          <Slider
            data-testid="progress-slider"
            value={[pos]}
            max={cur?.length || 1}
            step={1000}
            disabled={!cur || cur.is_stream}
            onValueCommit={([v]) => act("seek", v)}
          />
          <div className="flex justify-between mt-2 text-xs font-mono text-slate-500">
            <span data-testid="now-playing-position">{fmt(pos)}</span>
            <span data-testid="now-playing-duration">{cur?.is_stream ? "LIVE" : fmt(cur?.length || 0)}</span>
          </div>
        </div>

        <PlayerControls player={player} act={act} />
      </div>
    </section>
  );
}
